import { useQuery } from "@tanstack/react-query";
import { followedScoutingQueryOptions, followedPlayerIdsQueryOptions } from "@/api/followedPlayers";
import { useScoutingContext } from "../page";
import { ScoutingPlayersTable } from "@/components/general/Scouting/ScoutingPlayersTable";
import { ScoutingGoalkeepersTable } from "@/components/general/Scouting/ScoutingGoalkeepersTable";
import { Bookmark } from "lucide-react";

export const ScoutingFollowedPage = () => {
    const { selectedTournamentId, tournaments } = useScoutingContext();
    const isTournamentsLoading = tournaments === undefined;

    const { data, isLoading } = useQuery({
        ...followedScoutingQueryOptions(selectedTournamentId || 0),
        enabled: !!selectedTournamentId && selectedTournamentId > 0,
    });

    const { data: followedPlayerIds, isLoading: isFollowedIdsLoading } = useQuery(followedPlayerIdsQueryOptions());

    const players = data?.players ?? [];
    const goalkeepers = data?.goalKeepers ?? [];
    const hasData = players.length > 0 || goalkeepers.length > 0;

    if (!isLoading && !isTournamentsLoading && !isFollowedIdsLoading && (followedPlayerIds?.length ?? 0) === 0) {
        return (
            <div className="flex flex-col items-center justify-center h-48 gap-3 text-on-surface-variant">
                <Bookmark className="h-10 w-10 opacity-30" />
                <p className="text-sm">You haven't followed any players yet.</p>
                <p className="text-xs opacity-70">
                    Click the bookmark icon next to a player in the Players or Goalkeepers tab to follow them.
                </p>
            </div>
        );
    }

    if (
        !isLoading &&
        !isTournamentsLoading &&
        !isFollowedIdsLoading &&
        !hasData &&
        (followedPlayerIds?.length ?? 0) > 0
    ) {
        return (
            <div className="flex flex-col items-center justify-center h-48 gap-3 text-on-surface-variant">
                <Bookmark className="h-10 w-10 opacity-30" />
                <p className="text-sm">Your followed players have no data in the selected tournament.</p>
                <p className="text-xs opacity-70">Try selecting a different tournament.</p>
            </div>
        );
    }

    return (
        <div className="flex flex-col min-h-0 h-full gap-4">
            {goalkeepers.length > 0 && (
                <div className="flex flex-col flex-1 min-h-0">
                    {players.length > 0 && (
                        <h2 className="text-xs font-bold uppercase tracking-wider text-on-surface-variant mb-2 px-1">
                            Goalkeepers
                        </h2>
                    )}
                    <div className="flex flex-col flex-1 min-h-0 rounded-md overflow-hidden border-table-border-color">
                        <ScoutingGoalkeepersTable
                            data={goalkeepers}
                            isLoading={isLoading || isTournamentsLoading}
                            selectedCategory="overview"
                            referenceData={data?.goalkeeperReferenceData}
                            followedPlayerIds={followedPlayerIds}
                        />
                    </div>
                </div>
            )}
            {(players.length > 0 || isLoading) && (
                <div className="flex flex-col flex-1 min-h-0">
                    {goalkeepers.length > 0 && (
                        <h2 className="text-xs font-bold uppercase tracking-wider text-on-surface-variant mb-2 px-1">
                            Field Players
                        </h2>
                    )}
                    <div className="flex flex-col flex-1 min-h-0 rounded-md overflow-hidden border-table-border-color">
                        <ScoutingPlayersTable
                            data={players}
                            isLoading={isLoading || isTournamentsLoading}
                            selectedCategory="overview"
                            referenceData={data?.playerReferenceData}
                            followedPlayerIds={followedPlayerIds}
                        />
                    </div>
                </div>
            )}
        </div>
    );
};
